<template>
  <EditorPopover
    ref="popover"
    :actionButtons="actionButtons"
    :title="title"
    @cancel="cancelEditing"
    @action="onAction">

    <TabbedBox>
      <template v-slot:tablist>
        <Tab :first="true" tabid="tab-selection" ref="selectionTab">
          <i class="far fa-object-group"></i> Selection
        </Tab>
        <Tab tabid="tab-group" v-if="isGroupAvailable" ref="groupTab">
          <i class="far fa-object-group"></i> Group
        </Tab>
        <Tab tabid="tab-vertex" v-if="isVertexAvailable" ref="vertexTab">
          <i class="far fa-dot-circle"></i> Vertex
        </Tab>
        <Tab tabid="tab-edgework" v-if="isEdgeworkAvailable" ref="edgeworkTab">
          <i class="far fa-border-outer"></i> Edgework
        </Tab>
        <Tab tabid="tab-cutout" v-if="isCutoutEditAvailable" ref="cutoutTab">
          <i class="far fa-draw-polygon"></i> Cutout
        </Tab>
        <tab tabid="tab-center-cutout" v-if="isCutoutCenterEditAvailable" ref="centerCutoutTab">
          <i class="far fa-circle"></i> Center Cutout
        </tab>
        <Tab tabid="tab-arc" v-if="isArcEditAvailable" ref="arcTab">
          <i class="far fa-draw-circle"></i> Arc
        </Tab>
        <Tab tabid="tab-datum" v-if="isDatumEditAvailable" ref="datumTab">
          <i class="far fa-ruler-vertical"></i> Datum Line
        </Tab>
        <Tab tabid="tab-tools" v-if="isDeveloperUser" ref="developerTab">
          <i class="far fa-tools"></i> Developer
        </Tab>
      </template>

      <template v-slot:tabcontent>
        <TabPage :first="true" tabid="tab-selection">
          Selection<br />
          {{toolInfoText}}
          <EditorPopoverButtonList v-if="isEditingAvailable">
            <EditorPopoverButton title="Remove" type="remove" icon="fa-trash-alt" @click="popoverActionButtonClick('remove')" v-if="isRemoveAvailable" />
            <EditorPopoverButton title="Clone" type="action" icon="fa-plus-square" v-if="isCloneAvailable" @click="popoverActionButtonClick('clone')" />
            <EditorPopoverButton title="Create Group" type="action" icon="fa-object-group" v-if="isGroupCreateAvailable" @click="popoverActionButtonClick('createGroup')" />
            <EditorPopoverButton title="Open Group" type="action" icon="fa-object-ungroup" v-if="isGroupOpenAvailable" @click="popoverActionButtonClick('openGroup')" />
            <EditorPopoverButton title="Combine Cutouts" type="action" icon="fa-layer-plus" v-if="isCutoutCombineAvailable" @click="popoverActionButtonClick('combineCutout')" />

            <EditorPopoverButton title="Create Cutout" type="action" icon="fa-folder-plus" v-if="isCutoutAvailable" @click="popoverActionButtonClick('addShapeCutout')" />
            <EditorPopoverButton title="Create Center Cutout" type="action" icon="fa-plus-circle" v-if="isCutoutCenterAvailable" @click="popoverActionButtonClick('addCenterCutout')" />
            <EditorPopoverButton title="Create Shape" type="action" icon="fa-plus-hexagon" v-if="isAddLibraryShapeAvailable" @click="popoverActionButtonClick('addShape')" />
            <EditorPopoverButton title="Create Kit" type="action" icon="fa-layer-plus" v-if="isAddLibraryKitAvailable" @click="popoverActionButtonClick('addKit')" />
            
            <EditorPopoverButton title="Add Points" type="action" icon="fa-map-marker-plus" v-if="isAddPointsAvailable" @click="popoverActionButtonClick('addPoints')" />
            <EditorPopoverButton title="Convert to arc" type="action" icon="fa-arrow-alt-circle-right" v-if="isConvertToArcAvailable" @click="popoverActionButtonClick('convertToArc')" />
            <EditorPopoverButton title="Convert to line" type="action" icon="fa-minus-circle" v-if="isConvertToLineAvailable" @click="popoverActionButtonClick('convertToLine')" />
            <EditorPopoverButton title="Corner to arc" type="action" icon="fa-arrow-alt-circle-right" v-if="isConvertVertexToArcAvailable" @click="popoverActionButtonClick('convertVertexToArc')" />
            <EditorPopoverButton title="Arc to corner" type="action" icon="fa-arrow-alt-circle-right" v-if="isConvertArcToVertexAvailable" @click="popoverActionButtonClick('convertArcToVertex')" />
          </EditorPopoverButtonList>
        </TabPage>

        <TabPage tabid="tab-group" v-if="isGroupAvailable">
          <div class="row no-gutters">
            <div class="col-6">
              <div class="row form-group">
                <div class="col-5">
                  <label class="col-form-label font-weight-bold">Measure To</label>
                </div>
                <div class="col-7">
                  <div v-if="isEditingAvailable">
                    <EditorPopoverOptionButton :active="glassShapeGroup.cutoutMeasurementType == CutoutMeasurementTypes.center" icon="fa-border-center-v" title="Center" @click="glassShapeGroup.cutoutMeasurementType = CutoutMeasurementTypes.center"></EditorPopoverOptionButton>
                    <EditorPopoverOptionButton :active="glassShapeGroup.cutoutMeasurementType == CutoutMeasurementTypes.edge" icon="fa-brackets" title="Edge" @click="glassShapeGroup.cutoutMeasurementType = CutoutMeasurementTypes.edge"></EditorPopoverOptionButton>
                  </div>
                  <div v-if="!isEditingAvailable">
                    {{formatLookupName(glassShapeGroup.cutoutMeasurementType, CutoutMeasurementTypeLookup)}}
                  </div>
                </div>
              </div>
              <div class="row form-group">
                <div class="col-5">
                  <label class="col-form-label font-weight-bold">Cutout Polished</label>
                </div>
                <div class="col-7">
                  <FormSwitch booleanMode v-model="glassShapeGroup.isGroupCutoutPolished" v-if="isEditingAvailable" />
                  <FormStaticLabel v-if="!isEditingAvailable">{{formatBoolean(glassShapeGroup.isGroupCutoutPolished)}}</FormStaticLabel>
                </div>
              </div>
              <div class="row form-group">
                <div class="col-5">
                  <label class="col-form-label font-weight-bold">Stock Override</label>
                </div>
                <div class="col-7">
                  <FormSwitch booleanMode v-model="glassShapeGroup.isCutoutStockOverride" v-if="isEditingAvailable" />
                  <FormStaticLabel v-if="!isEditingAvailable">{{formatBoolean(glassShapeGroup.isCutoutStockOverride)}}</FormStaticLabel>
                </div>
              </div>
            </div>
            <div class="col-6" v-show="glassShapeGroup.isCutoutStockOverride">
              <div class="row no-gutters mb-1">
                <div class="col-8"><strong>Service Stock Override</strong></div>
                <div class="col-4 text-right">
                  <FormButton class="btn btn-primary btn-xxs" @click="onGroupAddOverrideStock"><i class="far fa-plus"></i> Add Stock</FormButton>
                </div>
              </div>
              <Scroller maxHeight="80">
                <div v-for="(stock, index) of glassShapeGroup.cutoutStockOverrideList" :key="stock.key" class="row no-gutters mb-1">
                  <div class="col-10 pl-3 text-truncate">{{stock.code}} - {{stock.name}}</div>
                  <div class="col-2 text-right">
                    <FormButton class="btn btn-default btn-xxs" @click="groupOverrideRemoveStock(index)"><i class="far fa-trash-alt"></i></FormButton>
                  </div>
                </div>
                <div class="text-center text-muted font-italic" v-if="glassShapeGroup.cutoutStockOverrideList.length == 0">No stock items set</div>
              </Scroller>
              
            </div>
          </div>
          <EditorPopoverButtonList v-if="isEditingAvailable">
            <EditorPopoverButton title="Cancel" type="cancel" icon="fa-times" @click="cancelGroupEdit" />
            <EditorPopoverButton title="Apply" type="update" icon="fa-check" @click="applyGroupEdit" />
          </EditorPopoverButtonList>

          <Popover 
            ref="groupAddStockPopover"
            :showHeader="true"
            :appendToBody="true"
            headerTitle="Add Override Stock"
            sizeClass="popover-medium"
            placement="top"
          >
            <div class="px-2 pt-2">
              <FormInputStockCode v-model="glassShapeGroup.popoverStockId" :stockType="InventoryStockTypes.glassService" />
              <div class="row mt-3">
                <div class="col-6">
                  <button class="btn btn-default btn-sm btn-block" @click.prevent="groupOverrideStockCancel"><i class="far fa-times mr-2"></i> Cancel</button>
                </div>
                <div class="col-6">
                  <button class="btn btn-default btn-sm btn-block" @click.prevent="groupOverrideStockAdd"><i class="far fa-check status-approved mr-2"></i> Add Override</button>
                </div>
              </div>
            </div>

          </Popover>
        </TabPage>

        <TabPage tabid="tab-vertex" v-if="isVertexAvailable">
          <div class="row form-group">
            <div class="col-4">
              <label class="col-form-label font-weight-bold">Corner Radius</label>
            </div>
            <div class="col-6">
              <FormInputText type="number" v-model="vertexCornerRadiusValue" :readonly="!isEditingAvailable" addon="mm" />
            </div>
          </div>
          <div class="row form-group">
            <div class="col-4">
              <label class="col-form-label font-weight-bold">Corner Polished</label>
            </div>
            <div class="col-6">
              <FormSwitch booleanMode v-model="vertexCornerPolished" v-if="isEditingAvailable" />
              <FormStaticLabel v-if="!isEditingAvailable">{{formatBoolean(vertexCornerPolished)}}</FormStaticLabel>
            </div>
          </div>  
          <EditorPopoverButtonList v-if="isEditingAvailable">
            <EditorPopoverButton title="Cancel" type="cancel" icon="fa-times" @click="cancelVertexEdit" />
            <EditorPopoverButton title="Apply" type="update" icon="fa-check" @click="applyVertexEdit" />
          </EditorPopoverButtonList>        
        </TabPage>

        <TabPage tabid="tab-edgework" v-if="isEdgeworkAvailable">
          <div class="row form-group">
            <div class="col-2">
              <label class="col-form-label font-weight-bold">Edgework</label>
            </div>
            <div class="col-5">
              <FormSelect :options="edgeworkLookupList" v-model="edgeworkConfigId" v-if="!isEdgeworkMultipleSelected" :readonly="!isEditingAvailable" />
              <FormStaticLabel v-if="isEdgeworkMultipleSelected">Multiple Edgeworks Selected</FormStaticLabel>
            </div>
            <div class="col-5">
              <FormInputText type="number" v-model="edgeworkSettingValue" addon="mm" v-show="!isEdgeworkMultipleSelected && isEdgeworkSettingSize" :readonly="!isEditingAvailable" />
              <FormInputText type="number" v-model="edgeworkSettingValue" addon="°" v-show="!isEdgeworkMultipleSelected && isEdgeworkSettingAngle" :readonly="!isEditingAvailable" />
            </div>
          </div>
          <EditorPopoverButtonList v-if="isEditingAvailable">
            <EditorPopoverButton title="Remove Edgework" type="remove" icon="fa-trash-alt" :disabled="!isEdgeworkSelected" @click="removeEdgework" />
            <EditorPopoverButton title="Apply Edgework" type="update" icon="fa-check" v-show="!isEdgeworkMultipleSelected" @click="applyEdgework" />
          </EditorPopoverButtonList>
        </TabPage>

        <TabPage tabid="tab-cutout" v-if="isCutoutEditAvailable">
          <div class="row form-group">
            <div class="col-4">
              <label class="col-form-label font-weight-bold">Cutout Polished</label>
            </div>
            <div class="col-6">
              <FormSwitch booleanMode v-model="isCutoutPolished" v-if="isEditingAvailable" />
              <FormStaticLabel v-if="!isEditingAvailable">{{formatBoolean(isCutoutPolished)}}</FormStaticLabel>
            </div>
          </div>
          <EditorPopoverButtonList v-if="isEditingAvailable">
            <EditorPopoverButton title="Cancel" type="cancel" icon="fa-times" @click="cancelCutoutEdit" />
            <EditorPopoverButton title="Apply" type="update" icon="fa-check" @click="applyCutoutEdit" />
          </EditorPopoverButtonList>
        </TabPage>

        <TabPage tabid="tab-center-cutout" v-if="isCutoutCenterEditAvailable">
          <div class="row form-group" v-if="isCenterCutoutRectangle || isCenterCutoutOval">
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Height</label>
            </div>
            <div class="col-3">
              <FormInputText type="number" addon="mm" v-model="centerCutout.height" :readonly="!isEditingAvailable" />
            </div>
            <div class="col-3" v-if="isCenterCutoutRectangle">
              <label class="col-form-label font-weight-bold">Corner Radius</label>
            </div>
            <div class="col-3" v-if="isCenterCutoutRectangle">
              <FormInputText type="number" addon="mm" v-model="centerCutout.cornerRadius" :readonly="!isEditingAvailable" />
            </div>
          </div>
          <div class="row form-group" v-if="isCenterCutoutRectangle || isCenterCutoutOval">
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Width</label>
            </div>
            <div class="col-3">
              <FormInputText type="number" addon="mm" v-model="centerCutout.width" :readonly="!isEditingAvailable" />
            </div>
            <div class="col-3" v-if="isCenterCutoutRectangle">
              <label class="col-form-label font-weight-bold">Angle</label>
            </div>
            <div class="col-3" v-if="isCenterCutoutRectangle">
              <FormInputText type="number" addon="°" v-model="centerCutout.angle" :readonly="!isEditingAvailable" />
            </div>
          </div>
          <div class="row form-group" v-if="isCenterCutoutCircle">
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Diameter</label>
            </div>
            <div class="col-3">
              <FormInputText type="number" addon="mm" v-model="centerCutout.diameter" :readonly="!isEditingAvailable" />
            </div>
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Countersunk</label>
            </div>
            <div class="col-3">
              <FormSwitch booleanMode v-model="centerCutout.isCountersunk" v-if="isEditingAvailable" />
              <FormStaticLabel v-if="!isEditingAvailable">{{formatBoolean(centerCutout.isCountersunk)}}</FormStaticLabel>
            </div>
          </div>
          <div class="row form-group" v-if="isCenterCutoutCustom">
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Height</label>
            </div>
            <div class="col-3">
              <FormInputText type="number" addon="mm" :value="centerCutout.height" :readonly="true" />
            </div>
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Width</label>
            </div>
            <div class="col-3">
              <FormInputText type="number" addon="mm" v-model="centerCutout.width" :readonly="true" />
            </div>
          </div>
          <div class="row form-group">
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Cutout Polished</label>
            </div>
            <div class="col-3">
              <FormSwitch booleanMode v-model="centerCutout.isCutoutPolished" v-if="isEditingAvailable" />
              <FormStaticLabel v-if="!isEditingAvailable">{{formatBoolean(centerCutout.isCutoutPolished)}}</FormStaticLabel>
            </div>
            <div class="col-3" v-show="isCenterCutoutCircle && centerCutout.isCountersunk">
              <label class="col-form-label font-weight-bold">Countersunk Radius</label>
            </div>
            <div class="col-3" v-show="isCenterCutoutCircle && centerCutout.isCountersunk">
              <FormInputText type="number" addon="mm" v-model="centerCutout.countersunkRadius" :readonly="!isEditingAvailable" />
            </div>
            <div class="col-3" v-show="isCenterCutoutRectangle || isCenterCutoutCustom || isCenterCutoutOval">
              <label class="col-form-label font-weight-bold">Measure To</label>
            </div>
            <div class="col-3" v-show="isCenterCutoutRectangle || isCenterCutoutCustom || isCenterCutoutOval">
              <FormButton class="btn-block btn-sm btn-default" @click="editMeasureTo" :disabled="!isEditingAvailable">{{measureToDescription}}</FormButton>
            </div>
          </div>
          <div class="row form-group" v-if="isCenterCutoutCircle">
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Measure To</label>
            </div>
            <div class="col-3">
              <FormButton class="btn-block btn-sm btn-default" @click="editMeasureTo" :disabled="!isEditingAvailable">{{measureToDescription}}</FormButton>              
            </div>
          </div>

          <EditorPopoverButtonList v-if="isEditingAvailable">
            <EditorPopoverButton title="Cancel" type="cancel" icon="fa-times" @click="cancelCenterCutoutEdit" />
            <EditorPopoverButton title="Apply" type="update" icon="fa-check" @click="applyCenterCutoutEdit" />
          </EditorPopoverButtonList>

          <EditorMeasureToPopover
            ref="measureToPopover"
            title="Measure To"
            :horizontalValue="centerCutout.measureToHorizontal"
            @updateHorizontal="centerCutout.measureToHorizontal = $event"
            :verticalValue="centerCutout.measureToVertical"
            @updateVertical="centerCutout.measureToVertical = $event"
            />
        </TabPage>

        <TabPage tabid="tab-arc" v-if="isArcEditAvailable">
          <div class="row form-group">
            <div class="col-3" v-show="arcEditType == ArcEditTypes.radius">
              <label class="col-form-label font-weight-bold">Arc Radius</label>
            </div>
            <div class="col-4" v-show="arcEditType == ArcEditTypes.radius">
              <FormInputText type="number" v-model="arcRadius" addon="mm" :readonly="!isEditingAvailable" />
            </div>
            <div class="col-3" v-show="arcEditType == ArcEditTypes.arcHeight">
              <label class="col-form-label font-weight-bold">Arc Height</label>
            </div>
            <div class="col-4" v-show="arcEditType == ArcEditTypes.arcHeight">
              <FormInputText type="number" v-model="arcHeight" addon="mm" :readonly="!isEditingAvailable" />
            </div>
            <div class="col-2">
              <label class="col-form-label font-weight-bold">Outer Arc</label>
            </div>
            <div class="col-3 text-right">
              <FormSwitch booleanMode v-model="arcClockwise" v-if="isEditingAvailable" />
              <FormStaticLabel v-if="!isEditingAvailable">{{formatBoolean(arcClockwise)}}</FormStaticLabel>
            </div>
          </div>
          <div class="row form-group">
            <div class="col-3">
              <label class="col-form-label font-weight-bold">Arc Edit Type</label>
            </div>
            <div class="col-4">
              <EditorPopoverOptionButton :active="arcEditType == ArcEditTypes.radius" :icon="iconForArcEditType(ArcEditTypes.radius)" title="Radius" @click="arcEditType = ArcEditTypes.radius"></EditorPopoverOptionButton>
              <EditorPopoverOptionButton :active="arcEditType == ArcEditTypes.arcHeight" :icon="iconForArcEditType(ArcEditTypes.arcHeight)" title="Height" @click="arcEditType = ArcEditTypes.arcHeight"></EditorPopoverOptionButton>
            </div>
          </div>

          <EditorPopoverNotification :text="errorText" />

          <EditorPopoverButtonList v-if="isEditingAvailable">
            <EditorPopoverButton title="Cancel" type="cancel" icon="fa-times" @click="cancelArcEdit" />
            <EditorPopoverButton title="Apply Arc Edit" type="update" icon="fa-check" @click="applyArcEdit" />
          </EditorPopoverButtonList>
        </TabPage>


        <TabPage tabid="tab-datum" v-if="isDatumEditAvailable">
          <div class="row form-group">
            <div class="col-12">
              <EditorPopoverDataRow label="Datum Length">
                <span v-if="selectedDatumLineList.length > 1">Multiple lines selected</span>
                <span v-if="selectedDatumLineList.length == 1">{{formatDecimal(datumLineLength, 2)}}mm</span>
              </EditorPopoverDataRow>
            </div>
          </div>
          <div class="row no-gutters form-group mt-2">
            <div class="col-4">
              <label class="col-form-label font-weight-bold">Show Dimension</label>
            </div>
            <div class="col-8">
              <FormSwitch booleanMode v-model="datumLineIsDimensionDisplayed" v-if="isEditingAvailable" />
              <FormStaticLabel v-if="!isEditingAvailable">{{formatBoolean(datumLineIsDimensionDisplayed)}}</FormStaticLabel>
            </div>
          </div>

          <EditorPopoverButtonList v-if="isEditingAvailable">
            <EditorPopoverButton title="Cancel" type="cancel" icon="fa-times" @click="cancelDatumEdit" />
            <EditorPopoverButton title="Apply Datum Edit" type="update" icon="fa-check" @click="applyDatumEdit" />
          </EditorPopoverButtonList>
        </TabPage>


        <TabPage tabid="tab-tools" v-if="isDeveloperUser">
          <EditorPopoverButtonList>
            <EditorPopoverButton title="Developer: Log Selection" type="action" icon="fa-code" @click="popoverActionButtonClick('developerLogSelection')" />
            <EditorPopoverButton title="Developer: Round Selection" type="action" icon="fa-code" @click="popoverActionButtonClick('developerRoundSelection')" />
          </EditorPopoverButtonList>
        </TabPage>

      </template>
    </TabbedBox>

  </EditorPopover>
</template>


<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import Utils from '@/utils/utils'
import EditorPopover from '@glass-designer/components/editorPopover'
import EditorPopoverMixin from '@glass-designer/mixins/editorPopover'
import EditorKeypad from '@glass-designer/components/editorKeypad'
import EditorTypes from '@glass-designer/types'
import MathsUtils from '@/utils/mathsUtils'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import EditorPopoverDataRow from '@glass-designer/components/editorPopoverDataRow'
import EditorPopoverButtonList from '@glass-designer/components/popover/editorPopoverButtonList'
import EditorPopoverButton from '@glass-designer/components/popover/editorPopoverButton'
import EditorMeasureToPopover from '@glass-designer/components/editorMeasureToPopover'
import EditorPopoverNotification from '@glass-designer/components/editorPopoverNotification'
import EditorPopoverOptionButton from '@glass-designer/components/popover/editorPopoverOptionButton'

import Popover from '@/components/popover'

import { LibraryItemShapeTypes, HoleMeasurePointTypes, HoleMeasurePointTypeLookup, ArcEditTypes, ArcEditTypeLookup, CutoutMeasurementTypes, CutoutMeasurementTypeLookup } from '@glass-designer/types'
import { OrderEditorEdgeworkSettingTypes, InventoryStockTypes } from '@/store/types'

export default {
  name: 'EditorSelectionToolPopover',
  mixins: [EditorPopoverMixin],
  components: {
    EditorPopover,
    EditorKeypad,
    FormInputStockCode,
    EditorPopoverDataRow,
    EditorPopoverButtonList,
    EditorPopoverButton,
    EditorMeasureToPopover,
    EditorPopoverNotification,
    EditorPopoverOptionButton,
    Popover
  },
  props: {
    editingEnabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      editor: null,
      actionButtons: [],
      title: '',
      toolInfoText: '',
      errorText: '',

      isEditable: true,
      isEdgeworkAvailable: false,
      isVertexAvailable: false,
      isGroupAvailable: false,
      isCutoutEditAvailable: false,
      isCutoutCenterEditAvailable: false,
      isCutoutCombineAvailable: false,
      isRemoveAvailable: false,
      isCloneAvailable: false,
      isCutoutAvailable: false,
      isCutoutCenterAvailable: false,
      isAddPointsAvailable: false,
      isConvertToArcAvailable: false,
      isConvertToLineAvailable: false,
      isConvertVertexToArcAvailable: false,
      isConvertArcToVertexAvailable: false,
      isArcEditAvailable: false,
      isDatumEditAvailable: false,
      isAddLibraryShapeAvailable: false,
      isAddLibraryKitAvailable: false,
      isGroupCreateAvailable: false,
      isGroupOpenAvailable: false,

      selectedDatumLineList: [],
      datumLineIsDimensionDisplayed: false,
      edgeworkLookupList: [],
      selectedEdgeworkConfigIdList: [],
      selectedEdgeworkSettingList: [],
      edgeworkConfigId: 0,
      edgeworkSettingValue: 0,
      vertexCornerRadiusValue: 0,
      vertexCornerPolished: false,
      isCutoutPolished: false,
      arcRadius: 0,
      arcHeight: 0,
      arcClockwise: true,
      arcEditType: ArcEditTypes.radius,
      centerCutout: {
        holeType: null,
        width: 0,
        height: 0,
        cornerRadius: 0,
        radius: 0,
        diameter: 0,
        angle: 0,
        isCutoutPolished: false,
        isCountersunk: false,
        countersunkRadius: 0,
        measureToHorizontal: '',
        measureToVertical: ''
      },
      glassShapeGroup: {
        cutoutMeasurementType: CutoutMeasurementTypes.edge,
        cutoutPolished: false,
        isCutoutStockOverride: false,
        cutoutStockOverrideList: [],
        popoverStockId: 0
      },
    }
  },
  computed: {
    edgeworkSettingType() {
      var edgeworkInfo = this.edgeworkLookupList.find(item => item.value == this.edgeworkConfigId)
      if(edgeworkInfo)
        return edgeworkInfo.settingType

      return OrderEditorEdgeworkSettingTypes.none
    },
    isEdgeworkSettingSize() {
      return this.edgeworkSettingType == OrderEditorEdgeworkSettingTypes.size
    },
    isEdgeworkSettingAngle() {
      return this.edgeworkSettingType == OrderEditorEdgeworkSettingTypes.angle
    },
    isEdgeworkMultipleSelected() {
      return this.selectedEdgeworkConfigIdList.length > 1
    },
    isEdgeworkSelected() {
      return this.selectedEdgeworkConfigIdList.length > 0
    },
    isCenterCutoutRectangle() {
      return this.centerCutout.holeType == LibraryItemShapeTypes.rectangle
    },
    isCenterCutoutCircle() {
      return this.centerCutout.holeType == LibraryItemShapeTypes.circle
    },
    isCenterCutoutOval() {
      return this.centerCutout.holeType == LibraryItemShapeTypes.oval
    },
    isCenterCutoutCustom() {
      return this.centerCutout.holeType == LibraryItemShapeTypes.custom
    },
    measureToDescription() {
      let horz = Utils.getLookupName(HoleMeasurePointTypeLookup, this.centerCutout.measureToHorizontal)
      let vert = Utils.getLookupName(HoleMeasurePointTypeLookup, this.centerCutout.measureToVertical)

      return `${horz}, ${vert}`
    },
    datumLineLength() {
      if(this.selectedDatumLineList.length > 0)
        return this.selectedDatumLineList[0].getLength()
      return 0
    },
    isEditingAvailable() {
      return this.editingEnabled && this.isEditable
    }
  },
  watch: {
    arcEditType: function() {
      this.debouncedArcUpdateTool()
    },
    arcRadius: function() {
      this.debouncedArcUpdateTool()
    },
    arcHeight: function() {
      this.debouncedArcUpdateTool()
    },
    arcClockwise: function() {
      this.debouncedArcUpdateTool()
    }
  },
  created() {
    this.ArcEditTypes = ArcEditTypes
    this.ArcEditTypeLookup = ArcEditTypeLookup
    this.CutoutMeasurementTypes = CutoutMeasurementTypes
    this.CutoutMeasurementTypeLookup = CutoutMeasurementTypeLookup
    this.InventoryStockTypes = InventoryStockTypes

    this.debouncedUpdateTool = _.debounce(this._updateTool, 100)
    this.debouncedArcUpdateTool = _.debounce(this.updateArcEdit, 100)
  },
  methods: {
    setup(editor, options) {
      this.clearOptions()
      this.errorText = ''
      this.editor = editor

      options = _.defaults(options, {
        actionButtons: [],
        title: ''
      })

      this.edgeworkLookupList = _.concat([
        { value: 0, name: 'None', settingType: OrderEditorEdgeworkSettingTypes.none }
      ], editor.configurationData.edgeworkList.filter(edgeworkConfig => edgeworkConfig.isActive).map(edgeworkConfig => {
        return { value: edgeworkConfig.id, name: edgeworkConfig.name, settingType: edgeworkConfig.settingType }
      }))

      this.actionButtons = options.actionButtons
      this.title = options.title
      this.toolInfoText = options.infoText
      
      this.isEditable = !!options.isEditable
      this.isEdgeworkAvailable = !!options.isEdgeworkAvailable
      this.isVertexAvailable = !!options.isVertexAvailable
      this.isGroupAvailable = !!options.isGroupAvailable
      this.vertexCornerRadiusValue = MathsUtils.roundNumber(parseFloat(options.vertexCornerRadiusValue || 0), 1)
      this.vertexCornerPolished = !!options.vertexCornerPolished

      this.isCutoutEditAvailable = !!options.isCutoutEditAvailable
      this.isCutoutCenterEditAvailable = !!options.isCutoutCenterEditAvailable
      this.isCutoutCombineAvailable = !!options.isCutoutCombineAvailable
      this.isRemoveAvailable = !!options.isRemoveAvailable
      this.isCloneAvailable = !!options.isCloneAvailable
      this.isCutoutAvailable = !!options.isCutoutAvailable
      this.isCutoutCenterAvailable = !!options.isCutoutCenterAvailable
      this.isAddPointsAvailable = !!options.isAddPointsAvailable
      this.isConvertToArcAvailable = !!options.isConvertToArcAvailable
      this.isConvertToLineAvailable = !!options.isConvertToLineAvailable
      this.isConvertVertexToArcAvailable = !!options.isConvertVertexToArcAvailable
      this.isConvertArcToVertexAvailable = !!options.isConvertArcToVertexAvailable
      this.isArcEditAvailable = !!options.isArcEditAvailable
      this.isDatumEditAvailable = !!options.isDatumEditAvailable
      this.selectedDatumLineList = options.selectedDatumLineList || []
      this.isAddLibraryShapeAvailable = !!options.isAddLibraryShapeAvailable
      this.isAddLibraryKitAvailable = !!options.isAddLibraryKitAvailable
      
      this.isGroupCreateAvailable = !!options.isGroupCreateAvailable
      this.isGroupOpenAvailable = !!options.isGroupOpenAvailable
      
      
      this.selectedEdgeworkConfigIdList = options.selectedEdgeworkConfigIdList || []
      this.selectedEdgeworkSettingList = options.selectedEdgeworkSettingList || []

      if(options.centerCutoutSettings) {
        let cutoutSettings = _.cloneDeep(options.centerCutoutSettings)
        cutoutSettings.diameter = MathsUtils.roundNumber(parseFloat(cutoutSettings.radius) * 2)

        this.centerCutout = cutoutSettings
      }

      if(this.selectedEdgeworkConfigIdList.length == 1) {
        this.edgeworkConfigId = parseInt(this.selectedEdgeworkConfigIdList[0] || 0)
        this.edgeworkSettingValue = MathsUtils.roundNumber(parseFloat(options.selectedEdgeworkSettingList[0] || 0), 2)
      } else {
        this.edgeworkConfigId = 0
        this.edgeworkSettingValue = 0
      }

      this.datumLineIsDimensionDisplayed = false
      if(this.selectedDatumLineList.length > 0)
        this.datumLineIsDimensionDisplayed = this.selectedDatumLineList[0].isDimensionDisplayed

      this.arcRadius = MathsUtils.roundNumber(parseFloat(options.arcRadius || 0), 2)
      this.arcHeight = MathsUtils.roundNumber(parseFloat(options.arcHeight || 0), 2)
      this.arcEditType = options.arcEditType || ArcEditTypes.radius
      this.arcClockwise = !!options.arcClockwise

      this.isCutoutPolished = !!options.isCutoutPolished

      this.glassShapeGroup.cutoutMeasurementType = options.groupMeasureType || CutoutMeasurementTypes.edge
      this.glassShapeGroup.isGroupCutoutPolished = !!options.isGroupCutoutPolished
      this.glassShapeGroup.isCutoutStockOverride = false
      this.glassShapeGroup.cutoutStockOverrideList = []

      if(options.glassShapeGroup) {
        this.glassShapeGroup.isCutoutStockOverride = options.glassShapeGroup.isCutoutStockOverride
        this.glassShapeGroup.cutoutStockOverrideList = _.cloneDeep(options.glassShapeGroup.cutoutStockOverrideList || [])
        this.glassShapeGroup.cutoutStockOverrideList.forEach(stockInfo => {
          stockInfo.key = uuidv4()
        })
      }

      this.callbackContext = options.context
      this.updateCallback = options.updateCallback
      this.applyCallback = options.applyCallback
      this.actionCallback = options.actionCallback
      this.cancelCallback = options.cancelCallback

      this.$nextTick(() => {
        if(this.isGroupAvailable)
          this.$refs.groupTab.show()
        else if(this.isCutoutCenterEditAvailable)
          this.$refs.centerCutoutTab.show()
        else if(this.isDatumEditAvailable)
          this.$refs.datumTab.show()
        else
          this.$refs.selectionTab.show()
      })
    },
    clearOptions() {
      this.callbackContext = null
      this.updateCallback = null
      this.applyCallback = null
      this.actionCallback = null
      this.cancelCallback = null
    },
    displayToolPopover(containerEl, editor, displayOptions) {
      this.$refs.popover.displayToolPopover(containerEl, editor, displayOptions)
    },
    viewportUpdated(containerEl, editor) {
    },
    hide() {
      if(this.cancelCallback) {
        // Reset cancel CB so it only is called once when hiding popovers
        let cancelCB = this.cancelCallback
        this.cancelCallback = null

        cancelCB(this.callbackContext, false)
      }

      this.clearOptions()
      this.$refs.popover.hide()
    },
    cancelEditing() {
      if(this.cancelCallback)
        this.cancelCallback(this.callbackContext, true)

      this.clearOptions()
      this.hide()
    },
    onShortcutKey(code) {
      switch(code) {
        case 'Escape':
          this.cancelEditing()
          break
        case 'Delete':
        case 'Backspace':
          if(this.isRemoveAvailable && this.isEditingAvailable)
            this.popoverActionButtonClick('remove')
          break
      }
    },

    _updateTool() {
      this.updateTool({ apply: false })
    },
    onAction(eventInfo) {
      this.updateTool({ apply: true, actionTag: eventInfo })
    },

    // Group
    cancelGroupEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'cancelGroupEdit')
      }
    },
    applyGroupEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'applyGroupEdit', {
          cutoutMeasurementType: this.glassShapeGroup.cutoutMeasurementType,
          isGroupCutoutPolished: this.glassShapeGroup.isGroupCutoutPolished,
          isCutoutStockOverride: this.glassShapeGroup.isCutoutStockOverride,
          cutoutStockOverrideList: this.glassShapeGroup.cutoutStockOverrideList
        })
      }
    },
    onGroupAddOverrideStock(evt) {
      this.glassShapeGroup.popoverStockId = 0
      this.$refs.groupAddStockPopover.displayFromElement(evt.target)
    },
    groupOverrideStockCancel() {
      this.$refs.groupAddStockPopover.hidePopover()
    },
    groupOverrideStockAdd() {
      if(this.glassShapeGroup.popoverStockId) {
        let editorConfigData = this.editor.getEditorConfigurationData()
        let stockItem = editorConfigData.stockList.find(stock => stock.id == this.glassShapeGroup.popoverStockId)
        if(stockItem) {
          this.glassShapeGroup.cutoutStockOverrideList.push({
            key: uuidv4(),
            id: stockItem.id,
            code: stockItem.code,
            name: stockItem.name
          })
        }
      }
      this.$refs.groupAddStockPopover.hidePopover()
    },
    groupOverrideRemoveStock(index) {
      this.glassShapeGroup.cutoutStockOverrideList.splice(index, 1)
    },

    // Vertex
    cancelVertexEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'cancelVertexEdit')
      }
    },
    applyVertexEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'applyVertexEdit', { cornerRadius: MathsUtils.roundNumber(this.parseToolNumber(this.vertexCornerRadiusValue), 1), cornerPolished: this.vertexCornerPolished })
      }
    },

    // Edgeworks
    removeEdgework() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'removeEdgework')
        this.selectedEdgeworkConfigIdList = []
        this.selectedEdgeworkSettingList = []
        this.edgeworkConfigId = 0
        this.edgeworkSettingValue = 0
      }
    },
    applyEdgework() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'applyEdgework', { edgeworkConfigId: this.edgeworkConfigId, edgeworkSettingValue: MathsUtils.roundNumber(this.parseToolNumber(this.edgeworkSettingValue), 2) })
      }
    },

    // Arcs
    cancelArcEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'cancelArcEdit')
      }
    },
    updateArcEdit() {
      try {
        this.errorText = ''

        if(this.updateCallback) {
          this.updateCallback(this.callbackContext, 'updateArcEdit', {
            arcEditType: this.arcEditType,
            arcRadius: this.parseToolNumber(this.arcRadius),
            arcHeight: this.parseToolNumber(this.arcHeight),
            arcClockwise: this.arcClockwise
          })
        }
      } catch(err) {
        console.error(err)
        this.errorText = `${err.message}`
      }
    },
    applyArcEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'applyArcEdit', {
          arcEditType: this.arcEditType,
          arcRadius: this.parseToolNumber(this.arcRadius),
          arcHeight: this.parseToolNumber(this.arcHeight),
          arcClockwise: this.arcClockwise
        })
      }
    },
    iconForArcEditType(arcEditType) {
      switch(arcEditType) {
        case ArcEditTypes.radius:
          return 'fa-draw-circle'
        case ArcEditTypes.arcHeight:
          return 'fa-ruler-vertical'
        default:
          return 'fa-arrows'
      }
    },

    // Datum Lines
    cancelDatumEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'cancelDatumEdit')
      }
    },
    applyDatumEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'applyDatumEdit', { isDimensionDisplayed: this.datumLineIsDimensionDisplayed })
      }
    },

    // Cutouts
    cancelCutoutEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'cancelCutoutEdit')
      }
    },
    applyCutoutEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'applyCutoutEdit', { isCutoutPolished: this.isCutoutPolished })
      }
    },
    cancelCenterCutoutEdit() {
      if(this.actionCallback) {
        this.actionCallback(this.callbackContext, 'cancelCenterCutoutEdit')
      }
    },
    applyCenterCutoutEdit() {
      if(this.actionCallback) {
        this.centerCutout.radius = MathsUtils.roundNumber(this.parseToolNumber(this.centerCutout.diameter) / 2.0, 1)
        this.actionCallback(this.callbackContext, 'applyCenterCutoutEdit', this.centerCutout)
      }
    },
    editMeasureTo(ev) {
      this.$refs.measureToPopover.displayFromElement(ev.target)
    },

    updateTool(options = {}) {
      try {
        this.errorText = ''

        if(options.apply) {
          if(this.applyCallback) {
            this.applyCallback(this.callbackContext, {
              
            })
            this.hide()
          }
        } else {
          if(this.updateCallback) {
            this.updateCallback(this.callbackContext, {
              
            })
          }
        }

      } catch(err) {
        console.error(err)
        this.errorText = `${err.message}`
      }

    }
  }
}
</script>